import React from "react";
import { useHistory } from "react-router-dom";
import "./FostHR.css";
import Footer from "../../components/Footer";
import {
  FaChartLine,
  FaSitemap,
  FaUserPlus,
  FaUserCheck,
  FaComments,
  FaMoneyCheckAlt,
  FaBusinessTime,
  FaCogs,
  FaBoxes,
  FaRobot,
  FaChartBar,
  FaUserTie,
  FaUsers,
  FaProjectDiagram,
  FaClipboardCheck,
  FaUserCog,
  FaExclamationTriangle,
  FaLightbulb,
} from "react-icons/fa";
import hrmsSystemImg from "../../assets/Products/FostHR/HRMS System.jpg";
import hrImage from "../../assets/Products/FostHR/HR.avif";
import fostHrLogo from "../../assets/Home/FostHR.png";

const FostHR = () => {
  const history = useHistory();

  const lifecycle = ["Recruit", "Assimilate", "Develop", "Assess", "Retire"];

  const pillars = [
    "One Platform, One Source of Truth",
    "Real Leadership Dashboards",
    "Workforce Productivity Intelligence",
    "Built-In Governance",
    "Cost Optimization at Scale",
    "AI That Improves Speed & Consistency"
  ];

  const problems = [
    {
      title: "Disparate systems",
      text: "HR, recruitment, projects, payroll, and assets operate in silos, preventing a single source of truth."
    },
    {
      title: "Lack of integrated visibility",
      text: "No real-time view of headcount, productivity, hiring, or utilization."
    },
    {
      title: "Paper-driven HR processes",
      text: "Onboarding, verification, KYC, and approvals consume time."
    },
    {
      title: "Staffing delays",
      text: "Scheduling, feedback, and tracking become inconsistent across teams."
    },
    {
      title: "Compliance risk",
      text: "Approvals and payroll updates require audit trails and governance."
    }
  ];

  const solutions = [
    {
      title: "Real-time analytics",
      text: "Instant workforce productivity and utilization insights."
    },
    {
      title: "Role-aware dashboards",
      text: "Purpose-built views for leadership, HR, managers, and ops."
    },
    {
      title: "End-to-end workflows",
      text: "From hire, onboard, allocate, timesheet, payroll to audit."
    },
    {
      title: "AI where it matters",
      text: "CV/JD parsing, interview MOM analysis, chart insights, assessments."
    },
    {
      title: "Enterprise governance",
      text: "Audit logs, master data, and notifications across the platform."
    }
  ];

  const features = [
    { icon: <FaChartLine />, title: "Key Insights Panel", text: "Analytics for operations insights." },
    { icon: <FaSitemap />, title: "Organization Management", text: "Multi-level structure with location-based policies." },
    { icon: <FaUserPlus />, title: "Recruitment Management (ATS)", text: "Streamlined hiring with approvals, candidate control, and skill-based matching." },
    { icon: <FaUserCheck />, title: "Employee Lifecycle", text: "Centralized employee lifecycle and onboarding management." },
    { icon: <FaComments />, title: "Interview Management", text: "Structured interviews with scheduling, feedback, and offer." },
    { icon: <FaMoneyCheckAlt />, title: "Payroll Management", text: "Payroll and payslip management with automated calculations." },
    { icon: <FaBusinessTime />, title: "Timesheet Management", text: "Tracked workflows with audit trails and time classification." },
    { icon: <FaCogs />, title: "Master Setup & Governance", text: "Audit logs, centralized master data, and smart in-app notifications." },
    { icon: <FaBoxes />, title: "Asset Management", text: "Complete asset lifecycle with self-service tracking and support." },
    { icon: <FaRobot />, title: "AI Features", text: "AI-driven hiring insights, parsing, and assessments." },
    { icon: <FaChartBar />, title: "Productivity & Workforce Analytics", text: "MVA insights optimize productivity, staffing, and costs." }
  ];

  const audiences = [
    { icon: <FaUserTie />, text: "HR, delivery, and compliance visibility" },
    { icon: <FaUsers />, text: "HR teams managing lifecycle and payroll" },
    { icon: <FaProjectDiagram />, text: "PMOs tracking utilization and staffing" },
    { icon: <FaClipboardCheck />, text: "Compliance & ops leaders managing governance" },
    { icon: <FaUserCog />, text: "COOs & delivery leaders optimizing workforce productivity" }
  ];

  return (
    <div className="fost-hr-page">
      {/* Hero */}
      <section className="fost-hr-hero">
        <div className="fost-hr-hero-inner">
          <div className="fost-hr-hero-text">
            <img src={fostHrLogo} alt="FostHR" className="fost-hr-hero-logo" />
            <p className="fost-hr-eyebrow">RADAR</p>
            <h1 className="fost-hr-hero-title">
              Unified Workforce Intelligence <span>Built Into Every Workflow</span>
            </h1>
            <p className="fost-hr-hero-desc">
              Recruit, assimilate, develop, assess, retire — one platform that connects
              HR, delivery, time tracking, payroll, and governance into a single source of truth.
            </p>
            <div className="fost-hr-hero-actions">
              <button className="fost-hr-cta" onClick={() => history.push("/contact")}>
                Request a Demo
              </button>
            </div>
          </div>
          <div className="fost-hr-hero-visual">
            <img src={hrImage} alt="FostHR workforce intelligence" className="fost-hr-hero-img" />
          </div>
        </div>
        {/* Lifecycle strip */}
        <div className="fost-hr-lifecycle">
          {lifecycle.map((step, idx) => (
            <React.Fragment key={step}>
              <span className="fost-hr-lifecycle-step">{step}</span>
              {idx < lifecycle.length - 1 && <span className="fost-hr-lifecycle-arrow">&#8594;</span>}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* Pillars */}
      <section className="fost-hr-pillars">
        <div className="fost-hr-container">
          <div className="fost-hr-pillars-grid">
            {pillars.map((pillar, idx) => (
              <div key={idx} className="fost-hr-pillar-card">
                <span className="fost-hr-pillar-num">{idx + 1}</span>
                <p className="fost-hr-pillar-text">{pillar}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problems vs Solutions */}
      <section className="fost-hr-vs">
        <div className="fost-hr-container">
          <div className="fost-hr-vs-grid">
            <div className="fost-hr-vs-col fost-hr-problems">
              <div className="fost-hr-vs-head">
                <FaExclamationTriangle className="fost-hr-vs-icon" />
                <h2>Problems</h2>
              </div>
              <p className="fost-hr-vs-lead">Is your HR + Delivery + Time Tracking scattered across tools?</p>
              <ul>
                {problems.map((item, idx) => (
                  <li key={idx}>
                    <strong>{item.title}</strong>
                    <span>{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="fost-hr-vs-col fost-hr-solutions">
              <div className="fost-hr-vs-head">
                <FaLightbulb className="fost-hr-vs-icon" />
                <h2>Solutions</h2>
              </div>
              <p className="fost-hr-vs-lead">Real-time analytics for workforce productivity and utilization insights.</p>
              <ul>
                {solutions.map((item, idx) => (
                  <li key={idx}>
                    <strong>{item.title}</strong>
                    <span>{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Image banner */}
      <section className="fost-hr-banner">
        <div className="fost-hr-container fost-hr-banner-inner">
          <div className="fost-hr-banner-text">
            <h2>One window into your entire workforce</h2>
            <p>
              From onboarding to asset allocation, FostHR keeps every team,
              every timesheet, and every approval in sync — so managers lead
              with clarity instead of chasing spreadsheets.
            </p>
          </div>
          <div className="fost-hr-banner-visual">
            <img src={hrmsSystemImg} alt="FostHR HRMS system" />
          </div>
        </div>
      </section>

      {/* Feature set */}
      <section className="fost-hr-features">
        <div className="fost-hr-container">
          <h2 className="fost-hr-section-title">A Feature Set That Drives Organizational Value</h2>
          <div className="fost-hr-features-grid">
            {features.map((feature, idx) => (
              <div key={idx} className="fost-hr-feature-card">
                <div className="fost-hr-feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Designed for */}
      <section className="fost-hr-audience">
        <div className="fost-hr-container">
          <h2 className="fost-hr-section-title">Designed For</h2>
          <div className="fost-hr-audience-grid">
            {audiences.map((audience, idx) => (
              <div key={idx} className="fost-hr-audience-card">
                <div className="fost-hr-audience-icon">{audience.icon}</div>
                <p>{audience.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="fost-hr-cta-section">
        <div className="fost-hr-container fost-hr-cta-inner">
          <h2>Ready to unify your workforce operations?</h2>
          <p>See how FostHR can bring HR, delivery, time tracking, and payroll into one intelligent platform.</p>
          <button className="fost-hr-cta" onClick={() => history.push("/contact")}>
            Get in Touch
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FostHR;
